import { InfoOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Result from 'components/v2/common/Result';
import { ProfileFields } from './profileFields';
import * as S from '../ClientProfile.styled';
import { CitizenshipData, PrevVisaData } from '../ClientProfile';

import { CandidatePersonalInfoData } from 'api/models/response/TableInput';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { RootState } from 'redux/store';
import { isDestinationCountry } from 'utils/countries';
import { shouldShowResidencyProof } from 'utils/residencyProof';

type AdditionalDetailsProps = {
    citizenshipData: CitizenshipData[] | null;
    personalInfoData: CandidatePersonalInfoData | null;
    prevVisaData: PrevVisaData[] | null;
    spouse: boolean;
    fam: boolean;
    mobile: boolean;
};

const AdditionalDetails = ({
    citizenshipData,
    personalInfoData,
    prevVisaData,
    spouse,
    fam,
    mobile,
}: AdditionalDetailsProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { hasCanada } = useEnabledCountries();
    // Region follows the row's country, not which markets the provider sells.
    const isInDestinationCountry = isDestinationCountry(personalInfoData?.country_of_residence);
    const showResidencyProof = shouldShowResidencyProof(
        hasCanada,
        personalInfoData?.country_of_residence,
        citizenshipData?.flatMap(({ country }) => (country ? [{ country }] : []))
    );
    function formatCurrency(amount: number | null) {
        if (amount) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(amount);
        } else {
            return '';
        }
    }

    function formatDate(date: Date | null) {
        if (date) {
            const stringDate = date.toString();
            const [year, month, day] = stringDate.split('-');
            const months =
                language === 'EN'
                    ? [
                          'January',
                          'February',
                          'March',
                          'April',
                          'May',
                          'June',
                          'July',
                          'August',
                          'September',
                          'October',
                          'November',
                          'December',
                      ]
                    : [
                          'Janvier',
                          'Février',
                          'Mars',
                          'Avril',
                          'Mai',
                          'Juin',
                          'Juillet',
                          'Août',
                          'Septembre',
                          'Octobre',
                          'Novembre',
                          'Décembre',
                      ];

            const monthName = months[parseInt(month, 10) - 1];
            const formattedDate = `${monthName} ${parseInt(day, 10)}, ${year}`;
            return formattedDate;
        } else {
            return '';
        }
    }

    return (
        <>
            {personalInfoData || (prevVisaData && prevVisaData.length > 0) ? (
                <>
                    <S.InfoItem>
                        <Tooltip title={t('gender', { lng: language })}>
                            <S.Label>{t('gender', { lng: language })}</S.Label>
                        </Tooltip>
                        <Tooltip title={personalInfoData?.gender}>
                            <S.Info mobile={mobile}>{personalInfoData?.gender}</S.Info>
                        </Tooltip>
                    </S.InfoItem>

                    <S.InfoItem>
                        <Tooltip title={t('maritalStatus', { lng: language })}>
                            <S.Label>{t('maritalStatus', { lng: language })}</S.Label>
                        </Tooltip>
                        <Tooltip title={personalInfoData?.marital_status}>
                            <S.Info mobile={mobile}>{personalInfoData?.marital_status}</S.Info>
                        </Tooltip>
                    </S.InfoItem>
                    <S.InfoItem>
                        <Tooltip title={t('dateOfBirth', { lng: language })}>
                            <S.Label mobile={mobile}>{t('dateOfBirth', { lng: language })}</S.Label>
                        </Tooltip>
                        {personalInfoData?.birth_date && (
                            <Tooltip title={formatDate(personalInfoData?.birth_date)}>
                                <S.Info mobile={mobile}>
                                    {formatDate(personalInfoData?.birth_date)}
                                </S.Info>
                            </Tooltip>
                        )}
                    </S.InfoItem>
                    <S.InfoItem>
                        <Tooltip title={t('countryOfResidence', { lng: language })}>
                            <S.Label mobile={mobile}>
                                {t('countryOfResidence', { lng: language })}
                            </S.Label>
                        </Tooltip>
                        <Tooltip title={personalInfoData?.country_of_residence}>
                            <S.Info mobile={mobile}>
                                {personalInfoData?.country_of_residence}
                            </S.Info>
                        </Tooltip>
                    </S.InfoItem>
                    {isInDestinationCountry && (
                        <S.InfoItem>
                            <Tooltip title={t('regionOfResidence', { lng: language })}>
                                <S.Label mobile={mobile}>
                                    {t('regionOfResidence', { lng: language })}
                                </S.Label>
                            </Tooltip>
                            <Tooltip title={personalInfoData?.region_of_residence}>
                                <S.Info mobile={mobile}>
                                    {personalInfoData?.region_of_residence}
                                </S.Info>
                            </Tooltip>
                        </S.InfoItem>
                    )}
                    <S.InfoItem>
                        <Tooltip title={t('statusInCountryOfResidence', { lng: language })}>
                            <S.Label mobile={mobile}>
                                {t('statusInCountryOfResidence', { lng: language })}
                            </S.Label>
                        </Tooltip>
                        <Tooltip title={personalInfoData?.residency_status}>
                            <S.Info mobile={mobile}>{personalInfoData?.residency_status}</S.Info>
                        </Tooltip>
                    </S.InfoItem>
                    <S.InfoItem>
                        <Tooltip title={t('residenceStatusSince', { lng: language })}>
                            <S.Label mobile={mobile}>
                                {t('residenceStatusSince', { lng: language })}
                            </S.Label>
                        </Tooltip>
                        {personalInfoData?.residence_since && (
                            <Tooltip title={formatDate(personalInfoData?.residence_since)}>
                                <S.Info mobile={mobile}>
                                    {formatDate(personalInfoData?.residence_since)}
                                </S.Info>
                            </Tooltip>
                        )}
                    </S.InfoItem>
                    <S.InfoItem>
                        <Tooltip title={t('driversLicense', { lng: language })}>
                            <S.Label mobile={mobile}>
                                {t('driversLicense', { lng: language })}
                            </S.Label>
                        </Tooltip>
                        <Tooltip title={personalInfoData?.driver_license}>
                            <S.Info mobile={mobile}>{personalInfoData?.driver_license}</S.Info>
                        </Tooltip>
                    </S.InfoItem>
                    <S.InfoItem>
                        <Tooltip title={t('class2withAirbrake', { lng: language })}>
                            <S.Label mobile={mobile}>
                                {t('class2withAirbrake', { lng: language })}
                            </S.Label>
                        </Tooltip>
                        <Tooltip
                            title={
                                personalInfoData?.class_2_with_airbrake
                                    ? t('yes', { lng: language })
                                    : t('no', { lng: language })
                            }
                        >
                            <S.Info mobile={mobile}>
                                {personalInfoData?.class_2_with_airbrake
                                    ? t('yes', { lng: language })
                                    : t('no', { lng: language })}
                            </S.Info>
                        </Tooltip>
                    </S.InfoItem>
                    <S.InfoItem>
                        <Tooltip title={t('approxYearlyIncome(USD)', { lng: language })}>
                            <S.Label mobile={mobile}>
                                {t('approxYearlyIncome(USD)', { lng: language })}
                            </S.Label>
                        </Tooltip>
                        {personalInfoData?.income && (
                            <Tooltip title={formatCurrency(personalInfoData?.income)}>
                                <S.Info mobile={mobile}>
                                    {formatCurrency(personalInfoData?.income)}
                                </S.Info>
                            </Tooltip>
                        )}
                    </S.InfoItem>
                    <S.InfoItem>
                        <Tooltip title={t('netWorth(USD)', { lng: language })}>
                            <S.Label mobile={mobile}>
                                {t('netWorth(USD)', { lng: language })}
                            </S.Label>
                        </Tooltip>
                        {personalInfoData?.net_worth && (
                            <Tooltip title={formatCurrency(personalInfoData?.net_worth)}>
                                <S.Info mobile={mobile}>
                                    {formatCurrency(personalInfoData?.net_worth)}
                                </S.Info>
                            </Tooltip>
                        )}
                    </S.InfoItem>
                    <S.InfoItem>
                        <Tooltip title={t('approxAvailableFunds(USD)', { lng: language })}>
                            <S.Label mobile={mobile}>
                                {t('approxAvailableFunds(USD)', { lng: language })}
                            </S.Label>
                        </Tooltip>
                        {personalInfoData?.liquid_assets && (
                            <Tooltip title={formatCurrency(personalInfoData?.liquid_assets)}>
                                <S.Info mobile={mobile}>
                                    {formatCurrency(personalInfoData?.liquid_assets)}
                                </S.Info>
                            </Tooltip>
                        )}
                    </S.InfoItem>
                    <ProfileFields
                        record={personalInfoData}
                        keys={['address', 'postal_code', 'city', 'currency', 'criminal_record']}
                        mobile={mobile}
                    />
                    {showResidencyProof && (
                        <ProfileFields
                            record={personalInfoData}
                            keys={['has_residency_proof']}
                            mobile={mobile}
                        />
                    )}
                    <S.InfoItem>
                        <Tooltip title={t('partner', { lng: language })}>
                            <S.Label>{t('partner', { lng: language })}</S.Label>
                        </Tooltip>
                        <Tooltip
                            title={
                                spouse ? t('yes', { lng: language }) : t('no', { lng: language })
                            }
                        >
                            <S.Info mobile={mobile}>
                                {spouse ? t('yes', { lng: language }) : t('no', { lng: language })}
                            </S.Info>
                        </Tooltip>
                    </S.InfoItem>
                    <S.InfoItem>
                        <Tooltip title={t('otherFamilyMembers', { lng: language })}>
                            <S.Label mobile={mobile}>
                                {t('otherFamilyMembers', { lng: language })}
                            </S.Label>
                        </Tooltip>
                        <Tooltip
                            title={fam ? t('yes', { lng: language }) : t('no', { lng: language })}
                        >
                            <S.Info mobile={mobile}>
                                {fam ? t('yes', { lng: language }) : t('no', { lng: language })}
                            </S.Info>
                        </Tooltip>
                    </S.InfoItem>
                    {prevVisaData && prevVisaData.length > 0 && (
                        <>
                            <S.Title>{t('previouslyHeldVisas', { lng: language })}</S.Title>
                            {prevVisaData.map((prevVisa: PrevVisaData, index: number) => (
                                <div key={index}>
                                    <S.InfoItem>
                                        <Tooltip title={t('name', { lng: language })}>
                                            <S.Label>{t('name', { lng: language })}</S.Label>
                                        </Tooltip>
                                        <Tooltip title={prevVisa.name}>
                                            <S.Info mobile={mobile}>{prevVisa.name}</S.Info>
                                        </Tooltip>
                                    </S.InfoItem>
                                    <S.InfoItem>
                                        <Tooltip title={t('startDate', { lng: language })}>
                                            <S.Label>{t('startDate', { lng: language })}</S.Label>
                                        </Tooltip>

                                        <Tooltip title={prevVisa.start}>
                                            <S.Info mobile={mobile}>{prevVisa.start}</S.Info>
                                        </Tooltip>
                                    </S.InfoItem>
                                    <S.InfoItem>
                                        <Tooltip title={t('endDate', { lng: language })}>
                                            <S.Label>{t('endDate', { lng: language })}</S.Label>
                                        </Tooltip>
                                        <Tooltip title={prevVisa.end}>
                                            <S.Info mobile={mobile}>{prevVisa.end}</S.Info>
                                        </Tooltip>
                                    </S.InfoItem>
                                    <S.InfoItem>
                                        <Tooltip title={t('country', { lng: language })}>
                                            <S.Label>{t('country', { lng: language })}</S.Label>
                                        </Tooltip>
                                        <Tooltip title={prevVisa.country}>
                                            <S.Info mobile={mobile}>{prevVisa.country}</S.Info>
                                        </Tooltip>
                                    </S.InfoItem>
                                </div>
                            ))}
                        </>
                    )}
                </>
            ) : (
                <Result
                    icon={<InfoOutlined sx={{ fontSize: 32 }} />}
                    title={t('noInfo', { lng: language })}
                    description="No additional details have been added yet."
                />
            )}
        </>
    );
};

export default AdditionalDetails;
